/* eslint-disable no-use-before-define */
import React, { useState, useEffect } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import Fuse from 'fuse.js'
import Pagination from 'react-js-pagination'
import 'array-flat-polyfill'

import styled from 'styled-components'
import searchIcon from '../img/icons/search.svg'
import chevronRight from '../img/icons/chevron-right-red.svg'
import GridFilter from './GridFilter'
import ProjectCard from './elements/ProjectCard'
import BoxDefaultOutline from './elements/BoxDefaultOutline'

const WithWedge = styled.div`
  /* &::before {
    content: "";
    position: absolute;
    top: -30px;
    left: 0;
    right: 0;
    margin: auto;
    background: #1C1C1C;
    width: 54px;
    height: 54px;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  } */
`

const FilterLabel = styled.div`
  font-size: ${(props) => props.theme.font.size.ll};
  color: ${(props) => props.theme.color.text.main};
  text-align: center;
  text-transform: uppercase;
  font-family: ${(props) => props.theme.font.family.secondary};
`

const SearchIcon = styled.img`
  width: 24px;
  height: 24px;
`

const SearchButton = styled.span`
  text-decoration: underline;
  cursor: pointer;
  &::after {
    content: '';
    background-image: url(${chevronRight});
    display: block;
    background-repeat: no-repeat;
    background-size: contain;
    width: 18px;
    height: 18px;
  }
`

const PaginationWrapper = styled.div`
  ul {
    margin-bottom: 0;
  }

  li {
    margin-right: 15px;
    position: relative;
    font-size: 20px;
    font-weight: 300;
    background-color: transparent;
    color: ${(props) => props.theme.color.text.contrast};
    border: 2px solid ${(props) => props.theme.color.contrast};
    padding: 6px 5px;
    padding-right: 6px;
    line-height: 1;

    &.active {
      a {
        color: #fff;
      }
      background-color: ${(props) => props.theme.color.contrast};
    }

    a {
      text-align: center;
      min-width: 20px;
      display: block;
      text-decoration: none;
    }

    &::after {
      content: '';
      display: block;
      width: 20px;
      height: 21px;
      position: absolute;
      right: -10px;
      bottom: -11px;

      transform: rotate(45deg);
      background: #fff;
      border-left: 2px solid ${(props) => props.theme.color.contrast};
    }
  }
`

const fuseOptions = {
  threshold: 0.2,
  keys: ['title', 'projectrecap.locatie'],
}

function Grid({ title, selectedFilters }) {
  const {
    allWpProject: { nodes: projects },
  } = useStaticQuery(graphql`
    query {
      allWpProject(sort: { order: DESC, fields: date }) {
        nodes {
          id
          title
          uri
          projectrecap {
            banner {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 480, height: 322, quality: 100)
                }
              }
            }
            locatie
            livestream {
              image {
                localFile {
                  childImageSharp {
                    gatsbyImageData(width: 255, quality: 100)
                  }
                }
              }
              iframe
            }
          }
          categories {
            nodes {
              id
              name
            }
          }
          renovatie {
            nodes {
              id
              name
            }
          }
          woningbouw {
            nodes {
              id
              name
            }
          }
          zakelijk {
            nodes {
              id
              name
            }
          }
          particulier {
            nodes {
              id
              name
            }
          }
          diversen {
            nodes {
              id
              name
            }
          }
        }
      }
    }
  `)

  const [activeFilters, setActiveFilters] = useState({
    categories: [],
    renovatie: [],
    woningbouw: [],
    zakelijk: [],
    particulier: [],
    diversen: [],
    search: '',
  })

  const [page, setPage] = useState(1)

  const setLocalPage = (edge) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('project-page-number', edge)
    }

    setPage(edge)
  }

  const maxPageItems = 6

  const backupProjects = projects
  const filteredProjects = triggerFilter()
  const filters = loadFilters()

  const visibileProjects = paginate(filteredProjects)
  const [isGridsOpen, setIsGridsOpen] = useState({
    category: true,
    renovatie: false,
    woningbouw: false,
    zakelijk: false,
    particulier: false,
    diversen: false,
  })

  useEffect(() => {
    if (selectedFilters && selectedFilters.length > 0) {
      const selected = { ...activeFilters }
      const newIsGridsOpen = { ...isGridsOpen }

      selectedFilters.forEach((filterName) => {
        if (filters[filterName]) {
          filters[filterName].forEach(({ name }) => {
            selected[filterName].push(name)
            newIsGridsOpen[filterName] = true
          })
        } else {
          Object.keys(filters).forEach((categoryName) => {
            const categoryFilter = filters[categoryName].find(
              ({ name }) =>
                name.toLowerCase() ===
                filterName.toLowerCase().replace('-', ' ')
            )

            if (categoryFilter) {
              selected[categoryName].push(categoryFilter.name)
              newIsGridsOpen[categoryName] = true
            }
          })
        }
      })

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('project-filters', JSON.stringify(selected))
      }

      setIsGridsOpen(newIsGridsOpen)
      setActiveFilters(selected)
    } else if (typeof localStorage !== 'undefined') {
      const projectFilters = localStorage.getItem('project-filters')
      const projectFiltersOpen = localStorage.getItem('project-filters-open')
      const projectPageNumber = localStorage.getItem('project-page-number')

      if (projectFilters) {
        const updatedProjectFilters = JSON.parse(projectFilters)

        if (!updatedProjectFilters.zakelijk) updatedProjectFilters.zakelijk = []
        if (!updatedProjectFilters.particulier)
          updatedProjectFilters.particulier = []
        if (!updatedProjectFilters.diversen) updatedProjectFilters.diversen = []

        setActiveFilters(updatedProjectFilters)
      }

      if (projectFiltersOpen) {
        setIsGridsOpen(JSON.parse(projectFiltersOpen))
      }

      if (projectPageNumber) {
        setPage(parseFloat(projectPageNumber))
      }
    }
  }, [])

  function loadFilters() {
    const categories = backupProjects
      .map((project) =>
        project.categories.nodes.map((category) => category.name)
      )
      .flat()

    const renovatie = backupProjects
      .map((project) =>
        project.renovatie.nodes.map((category) => category.name)
      )
      .flat()

    const woningbouw = backupProjects
      .map((project) =>
        project.woningbouw.nodes.map((category) => category.name)
      )
      .flat()

    const zakelijk = backupProjects
      .map((project) => project.zakelijk.nodes.map((category) => category.name))
      .flat()

    const particulier = backupProjects
      .map((project) =>
        project.particulier.nodes.map((category) => category.name)
      )
      .flat()

    const diversen = backupProjects
      .map((project) => project.diversen.nodes.map((category) => category.name))
      .flat()

    const projectFilters = {
      // new Set() returns unique values
      categories: [...new Set(categories)],
      renovatie: [...new Set(renovatie)],
      woningbouw: [...new Set(woningbouw)],
      zakelijk: [...new Set(zakelijk)],
      particulier: [...new Set(particulier)],
      diversen: [...new Set(diversen)],
    }

    projectFilters.categories = projectFilters.categories.map((item) =>
      addAmountToFilter(item, 'categories')
    )
    projectFilters.renovatie = projectFilters.renovatie.map((item) =>
      addAmountToFilter(item, 'renovatie')
    )
    projectFilters.woningbouw = projectFilters.woningbouw.map((item) =>
      addAmountToFilter(item, 'woningbouw')
    )
    projectFilters.zakelijk = projectFilters.zakelijk.map((item) =>
      addAmountToFilter(item, 'zakelijk')
    )
    projectFilters.particulier = projectFilters.particulier.map((item) =>
      addAmountToFilter(item, 'particulier')
    )
    projectFilters.diversen = projectFilters.diversen.map((item) =>
      addAmountToFilter(item, 'diversen')
    )

    return projectFilters
  }

  function triggerFilter() {
    let items = backupProjects.filter(filterCampagne)
    if (activeFilters.search !== '') {
      const fuse = new Fuse(items, fuseOptions)
      items = fuse.search(activeFilters.search).map((fuseItem) => fuseItem.item)
    }

    // const convertSortNumbers = items.map((item) => {
    //   let value = item.projectrecap.sortnumber

    //   if (value === null) {
    //     value = 100000
    //   }

    //   if (value === '') {
    //     value = 100000
    //   }

    //   item.projectrecap.sortnumber = parseInt(value)

    //   return item
    // })

    // return convertSortNumbers.sort(
    //   (a, b) => a.projectrecap.sortnumber - b.projectrecap.sortnumber
    // )

    return items
  }

  function addAmountToFilter(item, taxonomyType) {
    let count = 0

    backupProjects.forEach((project) => {
      if (
        project[taxonomyType] &&
        project[taxonomyType].nodes.length > 0 &&
        project[taxonomyType].nodes
          .map((taxonomy) => taxonomy.name)
          .includes(item)
      ) {
        // eslint-disable-next-line no-plusplus
        count++
      }
    })

    return {
      name: item,
      count,
    }
  }

  function filterCampagne(project) {
    let categorieMatches = false
    let renovatieMatches = false
    let woningbouwMatches = false
    let zakelijkMatches = false
    let particulierMatches = false
    let diversenMatches = false

    if (activeFilters.categories.length > 0) {
      project.categories.nodes.forEach((category) => {
        if (activeFilters.categories.includes(category.name)) {
          categorieMatches = true
        }
      })
    } else {
      categorieMatches = true
    }

    if (activeFilters.renovatie.length > 0) {
      project.renovatie.nodes.forEach((category) => {
        if (activeFilters.renovatie.includes(category.name)) {
          renovatieMatches = true
        }
      })
    } else {
      renovatieMatches = true
    }

    if (activeFilters.woningbouw.length > 0) {
      project.woningbouw.nodes.forEach((category) => {
        if (activeFilters.woningbouw.includes(category.name)) {
          woningbouwMatches = true
        }
      })
    } else {
      woningbouwMatches = true
    }

    if (activeFilters.zakelijk && activeFilters.zakelijk.length > 0) {
      project.zakelijk.nodes.forEach((category) => {
        if (activeFilters.zakelijk.includes(category.name)) {
          zakelijkMatches = true
        }
      })
    } else {
      zakelijkMatches = true
    }

    if (activeFilters.particulier && activeFilters.particulier.length > 0) {
      project.particulier.nodes.forEach((category) => {
        if (activeFilters.particulier.includes(category.name)) {
          particulierMatches = true
        }
      })
    } else {
      particulierMatches = true
    }

    if (activeFilters.diversen && activeFilters.diversen.length > 0) {
      project.diversen.nodes.forEach((category) => {
        if (activeFilters.diversen.includes(category.name)) {
          diversenMatches = true
        }
      })
    } else {
      diversenMatches = true
    }

    return (
      categorieMatches &&
      renovatieMatches &&
      woningbouwMatches &&
      zakelijkMatches &&
      particulierMatches &&
      diversenMatches
    )
  }

  function setActiveFilter(name, value) {
    const newActiveFilters = {
      ...activeFilters,
      [name]: value,
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('project-filters', JSON.stringify(newActiveFilters))
    }

    setActiveFilters(newActiveFilters)
  }

  function paginate(items) {
    let offset = 0
    if (page !== 1) {
      offset = (page - 1) * maxPageItems
    }

    return items.slice(offset, offset + maxPageItems)
  }

  return (
    <div className="container mb-lg-5">
      <div className="row">
        <div className="col-lg-4">
          <WithWedge className="position-relative p-4 color-background-greyAlt mb-5 pt-5 mt-n5">
            <FilterLabel className="filter-label with-dot font-weight-xl mb-2">
              Filters
            </FilterLabel>

            <GridFilter
              taxonomy={filters.zakelijk}
              activeFilterType={activeFilters.zakelijk}
              onChange={(values) => setActiveFilter('zakelijk', values)}
              onClear={() => setActiveFilter('zakelijk', [])}
              title="C3 zakelijk"
              plural="zakelijk"
              tag="zakelijk"
              max={5}
              isGridsOpen={isGridsOpen}
              setIsGridsOpen={setIsGridsOpen}
              setPage={setLocalPage}
            />

            <GridFilter
              taxonomy={filters.particulier}
              activeFilterType={activeFilters.particulier}
              onChange={(values) => setActiveFilter('particulier', values)}
              onClear={() => setActiveFilter('particulier', [])}
              title="C3 particulier"
              plural="particulier"
              tag="particulier"
              max={5}
              isGridsOpen={isGridsOpen}
              setIsGridsOpen={setIsGridsOpen}
              setPage={setLocalPage}
            />

            <GridFilter
              taxonomy={filters.diversen}
              activeFilterType={activeFilters.diversen}
              onChange={(values) => setActiveFilter('diversen', values)}
              onClear={() => setActiveFilter('diversen', [])}
              title="C3 diversen"
              plural="diversen"
              tag="diversen"
              max={5}
              isGridsOpen={isGridsOpen}
              setIsGridsOpen={setIsGridsOpen}
              setPage={setLocalPage}
            />
          </WithWedge>
        </div>

        <div className="col-lg-8 pt-4">
          <h2>{title}</h2>
          <div className="row">
            <div className="col-lg-10 mb-3">
              <BoxDefaultOutline
                contrast
                className="d-flex align-items-center pr-0"
              >
                <SearchIcon className="mr-3" src={searchIcon} alt="" />
                <input
                  onInput={(event) =>
                    setActiveFilter('search', event.target.value)
                  }
                  className="col border-0 font-italic font-size-m py-2"
                  type="search"
                  name="search"
                  value={activeFilters.search}
                  placeholder="Typ hier uw zoekterm(en)"
                />
                <SearchButton
                  className="color-text-contrast ml-3 font-size-m d-flex align-items-center"
                  role="button"
                >
                  <span className="mr-3" />
                </SearchButton>
              </BoxDefaultOutline>
            </div>
          </div>

          {/* <p className="color-text-grey font-italic">
            {filteredProjects.length} 
            {' '}
            {filteredProjects.length === 1 ? "project voldoet" : "projecten voldoen"}
            {' '}
            aan uw zoekopdracht
          </p> */}

          <div className="row">
            {visibileProjects.map((project) => (
              <div key={project.id} className="col-lg-6 mb-5">
                <ProjectCard
                  src={project.projectrecap.banner}
                  livestream={project.projectrecap.livestream}
                  location={project.projectrecap.locatie}
                  title={project.title}
                  link={{
                    title: 'Bekijk project >',
                    to: project.uri,
                  }}
                />
              </div>
            ))}
          </div>
          <div className="row">
            {filteredProjects.length > maxPageItems && (
              <PaginationWrapper className="col-lg-12 d-flex justify-content-end align-items-center">
                <span className="mr-4">
                  Pagina {page} van de{' '}
                  {Math.ceil(filteredProjects.length / maxPageItems)}
                </span>
                <Pagination
                  activePage={page}
                  itemsCountPerPage={maxPageItems}
                  totalItemsCount={filteredProjects.length}
                  pageRangeDisplayed={5}
                  itemClass="paginate-item"
                  onChange={setLocalPage}
                  hideNavigation
                  hideFirstLastPages
                />
              </PaginationWrapper>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Grid
